import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase, Category } from "@/lib/supabase";

const CategoriesSection = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("categories")
      .select("*")
      .order("count", { ascending: false })
      .then(({ data }) => {
        setCategories(data || []);
        setLoading(false);
      });
  }, []);

  return (
    <section className="py-20 lg:py-28 bg-white px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <span className="text-xs font-semibold tracking-[0.3em] uppercase text-transparent bg-clip-text bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a]">
            Discover
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mt-3 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Shop by Category
          </h2>
          <div className="w-16 h-0.5 bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] mx-auto rounded-full" />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className={`rounded-2xl bg-gray-100 animate-pulse ${i === 0 ? "sm:row-span-2 min-h-[320px]" : "h-52"}`} />
            ))}
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <i className="ri-folder-line text-4xl mb-3 block" />
            <p>No categories found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* Large card - first category */}
            <Link
              to="/products"
              className="sm:row-span-2 group relative overflow-hidden rounded-2xl cursor-pointer block"
            >
              <div className="h-64 sm:h-full min-h-[320px]">
                <img
                  src={categories[0].image}
                  alt={categories[0].name}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-white text-2xl font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {categories[0].name}
                  </h3>
                  <p className="text-white/80 text-sm mb-3">{categories[0].description}</p>
                  <span className="text-xs text-white/60">{categories[0].count} products</span>
                </div>
              </div>
            </Link>

            {/* Rest of categories */}
            {categories.slice(1).map((cat) => (
              <Link
                key={cat.id}
                to="/products"
                className="group relative overflow-hidden rounded-2xl cursor-pointer block h-52"
              >
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <div className="absolute bottom-4 left-5 right-5">
                  <h3 className="text-white text-lg font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {cat.name}
                  </h3>
                  <p className="text-white/75 text-xs mt-0.5">{cat.description}</p>
                </div>
                <div className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <i className="ri-arrow-right-up-line text-white text-sm" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default CategoriesSection;
